import {isNil} from 'ramda';

const LAYER_SELECTOR = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';
const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, [tabindex]';

const isVisible = (element: HTMLElement) => {
  if (!isNil(element.closest('[hidden], [inert], [aria-hidden="true"]'))) {
    return false;
  }
  let ancestor: HTMLElement | null = element;
  while (ancestor) {
    const style = getComputedStyle(ancestor);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    ancestor = ancestor.parentElement;
  }
  return true;
};

const hasOpenLayer = () =>
  Array.from(document.querySelectorAll<HTMLElement>(LAYER_SELECTOR)).some(isVisible);

const getFocusableElements = (surface: HTMLElement) =>
  Array.from(surface.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex >= 0 && !element.matches(':disabled') && isVisible(element)
  );

/** Portaled controls have their own stacking context and must retain focus above the grid. */
const isAboveSurface = (element: HTMLElement, surface: HTMLElement) => {
  const surfaceLayer = Number(getComputedStyle(surface).zIndex);
  let ancestor: HTMLElement | null = element;
  while (ancestor && ancestor !== document.body) {
    if (Number(getComputedStyle(ancestor).zIndex) > surfaceLayer) {
      return true;
    }
    ancestor = ancestor.parentElement;
  }
  return false;
};

/** Owns one maximized grid's browser resources; returns an idempotent teardown. */
export function activateGridMaximization(surface: HTMLElement, restore: VoidFunction) {
  const previousFocus = document.activeElement;
  const lockedAncestors: {
    element: HTMLElement;
    property: string;
    value: string;
    priority: string;
  }[] = [];
  let ancestor = surface.parentElement;
  while (ancestor) {
    const element = ancestor;
    // Preserve each axis: replacing the shorthand loses an existing overflow-y:auto.
    ['overflow', 'overflow-x', 'overflow-y'].forEach((property) => {
      lockedAncestors.push({
        element,
        property,
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property),
      });
    });
    ancestor.style.setProperty('overflow', 'hidden', 'important');
    ancestor = ancestor.parentElement;
  }

  let escapeOwnedByLayer = false;
  let isShiftTab = false;
  let isActive = true;

  const captureKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      // Snapshot before a child closes its menu/dialog during this same event.
      escapeOwnedByLayer = hasOpenLayer();
    }
    isShiftTab = event.key === 'Tab' && event.shiftKey;
  };

  const handleKey = (event: KeyboardEvent) => {
    if (
      event.key === 'Escape' &&
      !event.defaultPrevented &&
      !escapeOwnedByLayer &&
      event.target instanceof Node &&
      (surface.contains(event.target) ||
        event.target === document.body ||
        event.target === document.documentElement)
    ) {
      event.preventDefault();
      event.stopPropagation();
      restore();
    }
  };

  const containFocus = (event: FocusEvent) => {
    const target = event.target;
    if (
      !(target instanceof HTMLElement) ||
      surface.contains(target) ||
      !isNil(target.closest(LAYER_SELECTOR)) ||
      isAboveSurface(target, surface)
    ) {
      return;
    }
    const focusable = getFocusableElements(surface);
    const next = isShiftTab ? focusable.at(-1) : focusable[0];
    (next ?? surface).focus({preventScroll: true});
  };

  document.addEventListener('keydown', captureKey, true);
  document.addEventListener('keydown', handleKey);
  document.addEventListener('focusin', containFocus);
  surface.focus({preventScroll: true});

  return () => {
    if (!isActive) {
      return;
    }
    isActive = false;
    document.removeEventListener('keydown', captureKey, true);
    document.removeEventListener('keydown', handleKey);
    document.removeEventListener('focusin', containFocus);
    lockedAncestors.forEach(({element, property, value, priority}) => {
      element.style.setProperty(property, value, priority);
    });
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
      previousFocus.focus({preventScroll: true});
    }
  };
}
